/**
 * ISBNs and Amazon identifiers (round 6 quick entry). Pure string work, used
 * by the API (the /v1/books route) and the paste box alike.
 *
 * Handled without a network call:
 *  - a bare ISBN-10 or ISBN-13, dashes and spaces allowed, checksum-validated
 *  - an Amazon link (`amazon.<tld>/…/dp/<10>` or `/gp/product/<10>`) whose
 *    10-character token is a valid ISBN-10 — print books' ASINs are their ISBN-10
 *  - `openlibrary.org/isbn/<isbn>`
 * Not handled: `openlibrary.org/books/OL…M` (needs a fetch), Kindle ASINs
 * (`B0…`, Amazon exposes no lookup) — see `isKindleAsin`.
 */

const KINDLE_ASIN = /^B0[A-Z0-9]{8}$/i;

function isbn10Ok(d: string): boolean {
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const ch = d[i]!;
    const v = ch === "X" ? 10 : Number(ch);
    sum += v * (10 - i);
  }
  return sum % 11 === 0;
}

function isbn13Ok(d: string): boolean {
  let sum = 0;
  for (let i = 0; i < 13; i++) sum += Number(d[i]) * (i % 2 === 0 ? 1 : 3);
  return sum % 10 === 0;
}

/** Digits (and a trailing X) → a canonical, checksum-valid ISBN, or null. */
function validIsbn(digits: string): string | null {
  const u = digits.toUpperCase();
  if (/^97[89]\d{10}$/.test(u)) return isbn13Ok(u) ? u : null;
  if (/^\d{9}[\dX]$/.test(u)) return isbn10Ok(u) ? u : null;
  return null;
}

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

/** The 10-character product token of an Amazon link (`/dp/<10>`, `/gp/product/<10>`), or null. */
function amazonToken(u: URL): string | null {
  const host = u.hostname.toLowerCase();
  if (!/(^|\.)amazon\.[a-z.]{2,}$/.test(host)) return null;
  const m = u.pathname.match(/\/(?:dp|gp\/product)\/([0-9A-Za-z]{10})(?=\/|$)/);
  return m ? m[1]! : null;
}

/**
 * A pasted string → its ISBN (canonical digits, ISBN-13 with 978/979 or
 * ISBN-10 with a possible X), or null when the string carries no valid ISBN.
 */
export function isbnFromInput(s: string): string | null {
  const raw = s.trim();
  if (!raw) return null;
  if (/^[0-9Xx\s-]+$/.test(raw)) return validIsbn(raw.replace(/[\s-]/g, ""));
  const u = parseUrl(raw);
  if (!u) return null;
  const token = amazonToken(u);
  if (token) return validIsbn(token);
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  if (host === "openlibrary.org") {
    const m = u.pathname.match(/^\/isbn\/([0-9Xx-]+)/);
    if (m) return validIsbn(m[1]!.replace(/-/g, ""));
  }
  return null;
}

/** A Kindle edition's ASIN (`B0` + 8 alphanumerics), bare or inside an Amazon link. */
export function isKindleAsin(s: string): boolean {
  const raw = s.trim();
  if (KINDLE_ASIN.test(raw)) return true;
  const u = parseUrl(raw);
  if (!u) return false;
  const token = amazonToken(u);
  return token !== null && KINDLE_ASIN.test(token);
}
